import React from 'react';
import { Button, Grid } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import CustomBarChartWidget from 'components/Widgets/CustomBarchartWidget';
import PageWrapper from 'components/Common/PageWraper';
import PageHeader from 'components/Header/Page';
import { useTheme } from '@mui/material/styles';
import dayjs from 'dayjs';
import SegmentedBarChart from 'components/Widgets/SegmentedBarchartWidget';
import BarLineComboChart from 'components/Widgets/BarLineComboChart';
import RaceBarChart from 'components/Widgets/RaceBarChart';
import BarChartWidgetNew from 'components/Widgets/BarchartWidgetNew.tsx';
import ProgressChartWidgetNew from 'components/Widgets/ProgressChartWidgetNew';
import ThreeDBarChart from 'components/Widgets/ThreeDBarchart';

const consumedServicesData = [
  {
    name: 'Databricks',
    value: 5.08,
  },
  {
    name: 'Storage',
    value: 1.63,
  },
  {
    name: 'AKS (Computer)',
    value: 1.83,
  },
  {
    name: 'Data Factory',
    value: 0.49,
  },
  {
    name: '*Other Azure Services',
    value: 4.43,
  },
];

const consumedServicesColors = [
  '#8979FF',
  '#FF928A',
  '#3CC3DF',
  '#FFAE4C',
  '#00628B',
];

// SLA bar chart sample data
const sampleBarChartData = [
  { name: 'Item - A', value: 99.98 },
  { name: 'Item - B', value: 99.93 },
  { name: 'Item - C', value: 99.88 },
  { name: 'Item - D', value: 99.76 },
  { name: 'Item - E', value: 99.6 },
  { name: 'Item - F', value: 99.8 },
  { name: 'Item - G', value: 99.95 },
  { name: 'Item - H', value: 99.92 },
];

// bar + line combo chart sample data
const sampleComboData = [
  { name: 'DEC', others: 350, northAmerica: 92, international: 88 },
  { name: 'NOV', others: 650, northAmerica: 85, international: 91 },
  { name: 'OCT', others: 350, northAmerica: 78, international: 94 },
  { name: 'SEP', others: 650, northAmerica: 90, international: 83 },
  { name: 'AUG', others: 350, northAmerica: 73, international: 89 },
  { name: 'JUL', others: 650, northAmerica: 85, international: 76 },
  { name: 'JUN', others: 350, northAmerica: 81, international: 92 },
  { name: 'MAY', others: 650, northAmerica: 88, international: 79 },
  { name: 'APR', others: 650, northAmerica: 74, international: 95 },
  { name: 'MAR', others: 350, northAmerica: 86, international: 84 },
  { name: 'FEB', others: 650, northAmerica: 79, international: 71 },
];

// series config for the above sample data
const comboSeries = [
  {
    key: 'others',
    name: 'Others',
    type: 'bar',
    color: '#0D8EFF',
    yAxis: 'left',
  },
  {
    key: 'northAmerica',
    name: 'North America',
    type: 'line',
    color: '#FDB515',
    yAxis: 'right',
    smooth: true,
    symbolSize: 12,
  },
  {
    key: 'international',
    name: 'International',
    type: 'line',
    color: '#00C2A8',
    yAxis: 'right',
    smooth: true,
    symbolSize: 12,
  },
] as const;

// ---------------------------------------
// COLOR SCHEME FOR ABOVE/BELOW SLA
// ---------------------------------------
const barChartColorScheme = {
  above: '#4CAF50', // green for >= SLA
  below: '#F44336', // red for < SLA
};

// Sample API data for segmented bar chart
const apiData = [
  { YearMonth: '2025-06', moderate: 87, low: 239, veryLow: 3075 },
  { YearMonth: '2025-09', moderate: 46, low: 142, veryLow: 1880 },
];

const segmentedBarchartData = [
  { YearMonth: "2025-02", product01: 280, product02: 590, product03: 820 },
  { YearMonth: "2025-03", product01: 250, product02: 530, product03: 730 },
  { YearMonth: "2025-04", product01: 180, product02: 390, product03: 530 },
  { YearMonth: "2025-05", product01: 110, product02: 250, product03: 340 },
  { YearMonth: "2025-06", product01: 180, product02: 390, product03: 530 },
  { YearMonth: "2025-07", product01: 130, product02: 300, product03: 420 },
  { YearMonth: "2025-08", product01: 210, product02: 440, product03: 610 },
  { YearMonth: "2025-09", product01: 140, product02: 310, product03: 430 },
  { YearMonth: "2025-10", product01: 20,  product02: 40,  product03: 60 },
  { YearMonth: "2025-11", product01: 100, product02: 230, product03: 320 },
  { YearMonth: "2025-12", product01: 180, product02: 390, product03: 530 }
];

export default function BarChartsPage() {
  const navigate = useNavigate();
  const theme = useTheme();
  const colors = theme.palette;

  const getMonthLabel = (yearMonth: string) => dayjs(`${yearMonth}-01`).format('MMM YYYY');
  const categories = apiData.map((d) => getMonthLabel(d.YearMonth));

  const series = [
    {
      name: 'Very Low',
      data: apiData.map((d) => d.veryLow),
    },
    {
      name: 'Low',
      data: apiData.map((d) => d.low),
    },
    {
      name: 'Moderate',
      data: apiData.map((d) => d.moderate),
    },
  ];

  const category = segmentedBarchartData.map((d) => getMonthLabel(d.YearMonth));

  const seriesData = [
    {
      name: 'Product 01',
      data: segmentedBarchartData.map((d) => d.product01),
    },
    {
      name: 'Product 02',
      data: segmentedBarchartData.map((d) => d.product02),
    },
    {
      name: 'Product 03',
      data: segmentedBarchartData.map((d) => d.product03),
    },
  ];

  const demoCloudCostBarChart = [
    {
      '2023': {
        'Program 1': 16,
        'Program 2': 0,
        'Program 3': 4,
        'Program 4': 0,
        'Program 5': 3,
        'Program 6': 0,
        TotalCost: 23
      }
    },
    {
      '2024': {
        'Program 1': 16,
        'Program 2': 0,
        'Program 3': 4,
        'Program 4': 0,
        'Program 5': 3,
        'Program 6': 0,
        TotalCost: 23
      }
    },
    {
      '2025': {
        'Program 1': 16,
        'Program 2': 0,
        'Program 3': 4,
        'Program 4': 0,
        'Program 5': 3,
        'Program 6': 0,
        TotalCost: 23
      }
    }
  ];

  const demoFilteredSeries = [
    {
      name: 'Program 1',
      truncatedName: 'Program 1',
      dataKey: 'Program 1',
      color: '#33B5E5'
    },
    {
      name: 'Program 2',
      truncatedName: 'Program 2',
      dataKey: 'Program 2',
      color: '#39D353'
    },
    {
      name: 'Program 3',
      truncatedName: 'Program 3',
      dataKey: 'Program 3',
      color: '#FFB13B'
    },
    {
      name: 'Program 4',
      truncatedName: 'Program 4',
      dataKey: 'Program 4',
      color: '#FF7A45'
    },
    {
      name: 'Program 5',
      truncatedName: 'Program 5',
      dataKey: 'Program 5',
      color: '#F725B8'
    },
    {
      name: 'Program 6',
      truncatedName: 'Program 6',
      dataKey: 'Program 6',
      color: '#FF2D2D'
    }
  ];

  const demoCostProgressChartData = [
  {
    programname: 'Program Alpha',
    dev: 12000,
    qa: 8000,
    prod: 30000,
    total_cost: 50000
  },
  {
    programname: 'Program Beta',
    dev: 10000,
    qa: 6000,
    prod: 24000,
    total_cost: 40000
  },
  {
    programname: 'Program Gamma',
    dev: 7000,
    qa: 5000,
    prod: 18000,
    total_cost: 30000
  },
  {
    programname: 'Program Delta',
    dev: 6000,
    qa: 4000,
    prod: 12000,
    total_cost: 22000
  },
  {
    programname: 'Program Epsilon',
    dev: 4000,
    qa: 3000,
    prod: 9000,
    total_cost: 16000
  }
];

const demoCostProgressEnvNames = ['dev', 'qa', 'prod'];

const demoCostProgressEnvSeriesData = [
  {
    name: 'DEV',
    type: 'bar',
    stack: 'total',
    showBackground: true,
    backgroundStyle: {
      color: '#E4EDFF'
    },
    itemStyle: {
      color: '#00B4C5'
    },
    label: {
      show: false
    },
    data: [4000, 6000, 7000, 10000, 12000]
  },
  {
    name: 'QA',
    type: 'bar',
    stack: 'total',
    showBackground: true,
    backgroundStyle: {
      color: '#E4EDFF'
    },
    itemStyle: {
      color: '#00BF7D'
    },
    label: {
      show: false
    },
    data: [3000, 4000, 5000, 6000, 8000]
  },
  {
    name: 'PROD',
    type: 'bar',
    stack: 'total',
    showBackground: true,
    backgroundStyle: {
      color: '#E4EDFF'
    },
    itemStyle: {
      color: '#F57600'
    },
    label: {
      show: true,
      position: 'right',
      color: '#000000',
      clip: false,
      formatter: (params: any) => {
        const totals = [16000, 22000, 30000, 40000, 50000];
        const value = totals[params.dataIndex];

        if (value >= 1000000) {
          return `$${(value / 1000000).toFixed(1)}MM`;
        }

        return `$${(value / 1000).toFixed(0)}K`;
      }
    },
    data: [9000, 12000, 18000, 24000, 30000]
  }
];

const demoCostProgressAxisMax = 60000;

  return (
    <PageWrapper>
      <PageHeader title="Bar Charts Demo">
        <Button variant="pepwiseSecondary" onClick={() => navigate(-1)}>
          ← Back
        </Button>
      </PageHeader>

      <Grid container spacing={3} mt={2}>
        <Grid item xs={12} sm={12} md={6}>
          <CustomBarChartWidget
            title="Bar Chart with SLA"
            data={sampleBarChartData}
            isLoading={false}
            orientation="vertical"
            slaTarget={99.9}
            slaLabel="SLA"
            colorScheme={barChartColorScheme}
            barColor={colors.widget.colors.color2}
            showGridLines={true}
            onBarClick={(bar: any) => console.log('Clicked bar:', bar)}
            isCost={false}
            isHappySignal={false}
            hasTooltip={true}
            tooltipLabel="This example shows a bar chart with an SLA line with orientation set to vertical and above/below coloring."
          />
        </Grid>
        <Grid item xs={12} sm={12} md={6}>
          <CustomBarChartWidget
            title="Bar Chart with SLA"
            data={sampleBarChartData}
            isLoading={false}
            orientation="horizontal"
            slaTarget={99.9}
            slaLabel="SLA"
            colorScheme={barChartColorScheme}
            barColor={colors.widget.colors.color2}
            showGridLines={true}
            onBarClick={(bar: any) => console.log('Clicked bar:', bar)}
            isCost={false}
            isHappySignal={false}
            hasTooltip={true}
            tooltipLabel="This example shows a bar chart with an SLA line orientation set to horizontal and above/below coloring."
          />
        </Grid>

        <Grid item xs={12} sm={12} md={6}>
          <SegmentedBarChart
            title="Configurable Bar Chart - Stacked Vertical"
            categories={categories}
            series={series}
            mode="stacked"
            orientation="vertical"
            colorScheme={['#2196F3', '#4CAF50', '#FFC107']}
            tooltipLabel="This example shows a Stacked bar chart with  orientation set to vertical."
            onBarClick={(bar: any) => console.log('Clicked bar:', bar)}
          />
          ;
        </Grid>
        <Grid item xs={12} sm={12} md={6}>
          <SegmentedBarChart
            title="Configurable Bar Chart - grouped Vertical"
            categories={categories}
            series={series}
            mode="grouped"
            orientation="vertical"
            colorScheme={['#2196F3', '#4CAF50', '#FFC107']}
            tooltipLabel="This example shows a grouped bar chart with  orientation set to vertical."
            onBarClick={(bar: any) => console.log('Clicked bar:', bar)}
          />
          ;
        </Grid>

        <Grid item xs={12} sm={12} md={6}>
          <SegmentedBarChart
            title="Configurable Bar Chart - grouped horizontal"
            categories={categories}
            series={series}
            mode="grouped"
            orientation="horizontal"
            colorScheme={['#2196F3', '#4CAF50', '#FFC107']}
            tooltipLabel="This example shows a grouped bar chart with  orientation set to horizontal."
            onBarClick={(bar: any) => console.log('Clicked bar:', bar)}
          />
          ;
        </Grid>

        <Grid item xs={12} sm={12} md={6}>
          <SegmentedBarChart
            title="Configurable Bar Chart - Stacked horizontal"
            categories={categories}
            series={series}
            mode="stacked"
            orientation="horizontal"
            colorScheme={['#2196F3', '#4CAF50', '#FFC107']}
            tooltipLabel="This example shows a Stacked bar chart with  orientation set to horizontal."
            onBarClick={(bar: any) => console.log('Clicked bar:', bar)}
          />
          ;
        </Grid>

        <Grid item xs={12} sm={12} md={6}>
          <BarLineComboChart
            title="Monthly Comparison Chart for Regional"
            subTitle="Region"
            data={sampleComboData}
            isLoading={false}
            isCost={true}
            xKey="name"
            xAxisLabel="Month"
            series={[...comboSeries]}
            showGridLines={false}
            hasTooltip={true}
            tooltipLabel="This is a reusable bar + line combo chart with dual Y axis."
            rightAxisType="percent"
            onBarClick={(params: any) => console.log('Clicked:', params)}
          />
          ;
        </Grid>

        <Grid item xs={12} sm={12} md={6}>
          <RaceBarChart
            title="Top Cost Drivers"
            valueType="cost"
            data={[
              { name: 'Service Alpha', value: 980000 },
              { name: 'Service Beta', value: 840000 },
              { name: 'Service Gamma', value: 720000 },
              { name: 'Service Delta', value: 610000 },
              { name: 'Service Epsilon', value: 480000 },
              { name: 'Service Zeta', value: 420_000 },
              { name: 'Service Eta', value: 280_000 },
              { name: 'Service Theta', value: 160_000 },
            ]}
            series={[{ key: 'value', name: 'Cost', color: '#00B4C5' }]}
          />
        </Grid>

        <Grid item xs={12} sm={12} md={6}>
          <RaceBarChart
            title="Top 10 Storage Consumers"
            subTitle="By Product"
            valueType="number"
            data={[
              { name: 'App Alpha', primary: 2048, secondary: 4096, archive: 8192 },
              { name: 'App Beta', primary: 1024, secondary: 3072, archive: 6144 },
              { name: 'App Gamma', primary: 512, secondary: 2048, archive: 5120 },
              { name: 'App Delta', primary: 256, secondary: 1536, archive: 4096 },
              { name: 'App Epsilon', primary: 128, secondary: 1024, archive: 3072 },
              { name: 'App Theta', primary: 96, secondary: 768, archive: 2560 },
              { name: 'App Iota', primary: 64, secondary: 512, archive: 2048 },
              { name: 'App Kappa', primary: 32, secondary: 384, archive: 1536 },
            ]}
            series={[
              { key: 'primary', name: 'Primary', color: '#00B4C5' },
              { key: 'secondary', name: 'Secondary', color: '#00BF7D' },
              { key: 'archive', name: 'Archive', color: '#8BABF1' },
            ]}
          />
        </Grid>
        <Grid item xs={12} sm={12} md={6}>
          <RaceBarChart
            title="Top 10 Storage Consumers"
            subTitle="Storage Usage (PB)"
            valueType="storage"
            storageUnit="PB"
            data={[
              { name: 'App Alpha', value: 2.8 * 1024 ** 5 },
              { name: 'App Beta', value: 2.1 * 1024 ** 5 },
              { name: 'App Gamma', value: 1.7 * 1024 ** 5 },
              { name: 'App Delta', value: 1.3 * 1024 ** 5 },
              { name: 'App Epsilon', value: 0.95 * 1024 ** 5 },
              { name: 'App Theta', value: 0.72 * 1024 ** 5 },
              { name: 'App Iota', value: 0.48 * 1024 ** 5 },
              { name: 'App Kappa', value: 0.31 * 1024 ** 5 },
            ]}
            series={[{ key: 'value', name: 'Storage', color: '#00BF7D' }]}
          />
        </Grid>
        <Grid item xs={12} sm={12} md={6}>
          <RaceBarChart
            title="Top 10 Storage Consumers"
            subTitle="Storage Usage (PB)"
            valueType="storage"
            storageUnit="PB"
            data={[
              {
                name: 'App Alpha',
                primary: 0.6 * 1024 ** 5,
                secondary: 0.9 * 1024 ** 5,
                archive: 1.3 * 1024 ** 5,
              },
              {
                name: 'App Beta',
                primary: 0.4 * 1024 ** 5,
                secondary: 0.7 * 1024 ** 5,
                archive: 1.0 * 1024 ** 5,
              },
              {
                name: 'App Gamma',
                primary: 0.3 * 1024 ** 5,
                secondary: 0.6 * 1024 ** 5,
                archive: 0.8 * 1024 ** 5,
              },
              {
                name: 'App Delta',
                primary: 0.25 * 1024 ** 5,
                secondary: 0.45 * 1024 ** 5,
                archive: 0.6 * 1024 ** 5,
              },
              {
                name: 'App Epsilon',
                primary: 0.2 * 1024 ** 5,
                secondary: 0.35 * 1024 ** 5,
                archive: 0.4 * 1024 ** 5,
              },
              {
                name: 'App Theta',
                primary: 0.15 * 1024 ** 5,
                secondary: 0.3 * 1024 ** 5,
                archive: 0.27 * 1024 ** 5,
              },
              {
                name: 'App Iota',
                primary: 0.1 * 1024 ** 5,
                secondary: 0.22 * 1024 ** 5,
                archive: 0.16 * 1024 ** 5,
              },
              {
                name: 'App Kappa',
                primary: 0.08 * 1024 ** 5,
                secondary: 0.15 * 1024 ** 5,
                archive: 0.08 * 1024 ** 5,
              },
            ]}
            series={[
              { key: 'primary', name: 'Primary', color: '#00B4C5' },
              { key: 'secondary', name: 'Secondary', color: '#00BF7D' },
              { key: 'archive', name: 'Archive', color: '#8BABF1' },
            ]}
          />
        </Grid>

        <Grid item xs={12} sm={12} md={6}>
          <SegmentedBarChart
            title='Distribution'
            subTitle='Product By Month'
            categories={category}
            series={seriesData}
            mode="grouped"
            orientation="vertical"
            isCost={true}
            showBarLabel={false}
            xAxisLabel='Month'
            colorScheme={['#00B4C5', '#00BF7D', '#8BABF1']}
            tooltipLabel="This example shows a grouped bar chart with  orientation set to vertical."
            onBarClick={(bar: any) => console.log('Clicked bar:', bar)}
          />
          ;
        </Grid>
        {/* Stacked barchart */}
        <Grid item xs={12} sm={12} md={6}>
          <BarChartWidgetNew
            isLoading={false}
            title= 'YoY Cost'
            data={demoCloudCostBarChart}
            seriesData={demoFilteredSeries}
            handleEvent={()=> undefined}
          />
        </Grid>

        {/* progress barchart */}
        <Grid item xs={12} sm={12} md={6}>
          <ProgressChartWidgetNew
            title="Top Programs by Cost"
            data={demoCostProgressChartData}
            envNames={demoCostProgressEnvNames}
            seriesData={demoCostProgressEnvSeriesData}
            isLoading={false}
            chartByCost={0}
            axisMax={demoCostProgressAxisMax}
          />
        </Grid>
        <Grid item xs={12} sm={12} md={6}>
          <ThreeDBarChart
            title="Total Spend by Consumed Services (millions)"
            data={consumedServicesData}
            barColors={consumedServicesColors}
            showGridLines
            showLegend
            showDataLabels
            barWidth={102}
            chartHeight={350}
            yAxisMax={6}
            yAxisInterval={1}
            decimalPlaces={2}
          />
        </Grid>
      </Grid>
    </PageWrapper>
  );
}
